import React, { useState } from 'react';
import { X, CheckCircle, ArrowRight, Sparkles } from 'lucide-react';

export default function MassageRecommendation({ onSelect, onClose }) {
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState({
    concern: '',
    areas: [],
    pressure: '',
    experience: ''
  });
  const [recommendation, setRecommendation] = useState(null);

  const concerns = [
    { value: 'pain', label: 'Muscle pain or tension', icon: '💪' },
    { value: 'stress', label: 'Stress and relaxation', icon: '🧘' },
    { value: 'swelling', label: 'Swelling or fluid retention', icon: '💧' },
    { value: 'sculpting', label: 'Body contouring', icon: '✨' },
    { value: 'wellness', label: 'General wellness', icon: '🌟' }
  ];

  const areas = [
    { value: 'neck', label: 'Neck and shoulders' },
    { value: 'back', label: 'Back' },
    { value: 'legs', label: 'Legs' },
    { value: 'full', label: 'Full body' }
  ];

  const pressures = [
    { value: 'light', label: 'Gentle and relaxing', icon: '🌸' },
    { value: 'medium', label: 'Moderate pressure', icon: '💆' },
    { value: 'firm', label: 'Deep and firm', icon: '💪' }
  ];

  const experiences = [
    { value: 'first', label: 'First time having a massage' },
    { value: 'occasional', label: 'Had a few massages before' },
    { value: 'regular', label: 'Regular massage client' }
  ];

  const handleConcernSelect = (value) => {
    setAnswers({ ...answers, concern: value });
    setStep(2);
  };

  const handleAreaToggle = (value) => {
    const newAreas = answers.areas.includes(value)
      ? answers.areas.filter(a => a !== value)
      : [...answers.areas, value];
    setAnswers({ ...answers, areas: newAreas });
  };

  const handlePressureSelect = (value) => {
    setAnswers({ ...answers, pressure: value });
    setStep(4);
  };

  const handleExperienceSelect = (value) => {
    setAnswers({ ...answers, experience: value });
    generateRecommendation(value);
  };

  const generateRecommendation = (experience) => {
    const { concern, areas, pressure } = answers;
    
    let service = '';
    let reason = [];
    let benefits = [];
    let alternative = '';

    // Logic for recommendations based on answers
    if (concern === 'pain') {
      if (pressure === 'firm' || pressure === 'medium') {
        service = 'Deep Tissue Massage';
        reason = [
          'Targets chronic muscle tension',
          'Releases deep knots and tightness',
          'Perfect for persistent pain relief'
        ];
        benefits = [
          'Immediate relief from muscle tension',
          'Improved posture and flexibility',
          'Long-lasting results'
        ];
        alternative = 'Swedish Massage for a gentler approach';
      } else {
        service = 'Swedish Massage';
        reason = [
          'Gentle relief for muscle tension',
          'Promotes relaxation while easing pain',
          'Perfect for sensitive areas'
        ];
        benefits = [
          'Reduces muscle tension gently',
          'Improves circulation',
          'Promotes overall relaxation'
        ];
        alternative = 'Deep Tissue if you want more intense pressure';
      }
    } else if (concern === 'stress') {
      service = 'Swedish Massage';
      reason = [
        'Classic relaxation technique',
        'Flowing strokes reduce stress',
        'Perfect for unwinding'
      ];
      benefits = [
        'Deep relaxation',
        'Reduces anxiety and stress',
        'Better sleep quality',
        'Improved mood'
      ];
      alternative = 'Deep Tissue if you also have muscle tension';
    } else if (concern === 'swelling') {
      service = 'Traditional Lymphatic Drainage';
      reason = [
        'Therapeutic treatment for swelling',
        'Gentle technique for fluid retention',
        'Medical-grade lymphatic drainage'
      ];
      benefits = [
        'Reduces swelling and inflammation',
        'Supports immune system',
        'Gentle and relaxing'
      ];
      alternative = 'Brazilian Lymphatic Drainage for body contouring too';
    } else if (concern === 'sculpting') {
      service = 'Brazilian Lymphatic Drainage';
      reason = [
        'Body contouring technique',
        'Reduces bloating and defines curves',
        'Visible results immediately'
      ];
      benefits = [
        'Sculpts and defines body contours',
        'Reduces bloating',
        'Improves skin tone',
        'Boosts confidence'
      ];
      alternative = 'Traditional Lymphatic Drainage for therapeutic benefits';
    } else {
      service = pressure === 'light' ? 'Swedish Massage' : 'Deep Tissue Massage';
      reason = [
        'Perfect for overall wellness',
        'Balances body and mind',
        'Regular maintenance for health'
      ];
      benefits = [
        'Improves overall wellbeing',
        'Boosts circulation',
        'Reduces stress',
        'Better sleep'
      ];
      alternative = pressure === 'light' ? 'Deep Tissue for more intensity' : 'Swedish for relaxation';
    }

    // Add area-specific notes
    if (areas.includes('neck')) {
      reason.push('Extra focus on neck and shoulders');
    }
    if (areas.includes('back')) {
      reason.push('Targets back tension effectively');
    }

    // Price
    const price = service === 'Swedish Massage' ? '£40' : service === 'Brazilian Lymphatic Drainage' ? '£60' : '£50';


      const serviceValue = service === 'Swedish Massage' ? 'swedish' : 
                    service === 'Deep Tissue Massage' ? 'deep-tissue' :
                    service === 'Traditional Lymphatic Drainage' ? 'lymphatic' :
                    'brazilian';

    setRecommendation({
      service,
      serviceValue,
      price,
      reason,
      benefits,
      alternative,
      duration: '60 minutes'
    });
    
    setStep(5);
  };

  const getAreaLabels = () => {
    if (answers.areas.length === 0) return 'various areas';
    return answers.areas.map(a => areas.find(area => area.value === a)?.label.toLowerCase()).join(' and ');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white p-6 rounded-t-2xl relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 hover:bg-white/20 rounded-full p-2 transition"
          >
            <X size={24} />
          </button>
          <div className="flex items-center space-x-3 mb-2">
            <Sparkles size={32} />
            <h2 className="text-2xl font-bold">Find Your Perfect Massage</h2>
          </div>
          <p className="text-teal-50">Answer a few quick questions for personalized recommendations</p>
          
          {/* Progress */}
          <div className="mt-4 flex space-x-2">
            {[1, 2, 3, 4].map((s) => (
              <div 
                key={s}
                className={`flex-1 h-1 rounded-full ${
                  step >= s ? 'bg-white' : 'bg-white/30'
                }`}
              />
            ))}
          </div>
        </div>

        <div className="p-6">
          {/* Step 1: Main Concern */}
          {step === 1 && (
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-gray-900 mb-4">What's your main concern today?</h3>
              <div className="space-y-3">
                {concerns.map((concern) => (
                  <button
                    key={concern.value}
                    onClick={() => handleConcernSelect(concern.value)}
                    className="w-full flex items-center space-x-4 p-4 border-2 border-gray-200 rounded-xl hover:border-teal-500 hover:bg-teal-50 transition text-left"
                  >
                    <span className="text-3xl">{concern.icon}</span>
                    <span className="text-lg font-medium text-gray-900">{concern.label}</span>
                    <ArrowRight className="ml-auto text-gray-400" size={20} />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Problem Areas */}
          {step === 2 && (
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Where do you feel tension or discomfort?</h3>
              <p className="text-gray-600 text-sm mb-4">Select all that apply</p>
              <div className="space-y-3">
                {areas.map((area) => (
                  <button
                    key={area.value}
                    onClick={() => handleAreaToggle(area.value)}
                    className={`w-full flex items-center space-x-4 p-4 border-2 rounded-xl transition text-left ${
                      answers.areas.includes(area.value)
                        ? 'border-teal-500 bg-teal-50'
                        : 'border-gray-200 hover:border-teal-300'
                    }`}
                  >
                    <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center ${
                      answers.areas.includes(area.value)
                        ? 'border-teal-500 bg-teal-500'
                        : 'border-gray-300'
                    }`}>
                      {answers.areas.includes(area.value) && (
                        <CheckCircle size={20} className="text-white" />
                      )}
                    </div>
                    <span className="text-lg font-medium text-gray-900">{area.label}</span>
                  </button>
                ))}
              </div>
              <button
                onClick={() => setStep(3)}
                disabled={answers.areas.length === 0}
                className="w-full mt-6 bg-teal-600 text-white py-3 rounded-lg font-semibold hover:bg-teal-700 transition disabled:bg-gray-300"
              >
                Continue
              </button>
            </div>
          )}

          {/* Step 3: Pressure Preference */}
          {step === 3 && (
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-gray-900 mb-4">What pressure do you prefer?</h3>
              <div className="space-y-3">
                {pressures.map((pressure) => (
                  <button
                    key={pressure.value}
                    onClick={() => handlePressureSelect(pressure.value)}
                    className="w-full flex items-center space-x-4 p-4 border-2 border-gray-200 rounded-xl hover:border-teal-500 hover:bg-teal-50 transition text-left"
                  >
                    <span className="text-3xl">{pressure.icon}</span>
                    <span className="text-lg font-medium text-gray-900">{pressure.label}</span>
                    <ArrowRight className="ml-auto text-gray-400" size={20} />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 4: Experience Level */}
          {step === 4 && (
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Your massage experience?</h3>
              <div className="space-y-3">
                {experiences.map((exp) => (
                  <button
                    key={exp.value}
                    onClick={() => handleExperienceSelect(exp.value)}
                    className="w-full flex items-center space-x-4 p-4 border-2 border-gray-200 rounded-xl hover:border-teal-500 hover:bg-teal-50 transition text-left"
                  >
                    <span className="text-lg font-medium text-gray-900">{exp.label}</span>
                    <ArrowRight className="ml-auto text-gray-400" size={20} />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 5: Recommendation */}
          {step === 5 && recommendation && (
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-6">
                <div className="flex items-start space-x-3 mb-4">
                  <Sparkles className="text-teal-600 flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      Recommended for You
                    </h3>
                    <p className="text-gray-600">Based on your answers, here's what I suggest:</p>
                  </div>
                </div>
              </div>

              <div className="border-2 border-teal-200 rounded-xl p-6 bg-white">
                <div className="flex justify-between items-start mb-4">
                  <h4 className="text-2xl font-bold text-teal-900">{recommendation.service}</h4>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-teal-600">{recommendation.price}</div>
                    <div className="text-sm text-gray-600">{recommendation.duration}</div>
                  </div>
                </div>

                <div className="mb-4">
                  <p className="font-semibold text-gray-900 mb-2">Why this is perfect for you:</p>
                  <ul className="space-y-2">
                    {recommendation.reason.map((r, i) => (
                      <li key={i} className="flex items-start space-x-2">
                        <span className="text-teal-600 mt-1">•</span>
                        <span className="text-gray-700">{r}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mb-4">
                  <p className="font-semibold text-gray-900 mb-2">Benefits you'll experience:</p>
                  <ul className="space-y-2">
                    {recommendation.benefits.map((b, i) => (
                      <li key={i} className="flex items-start space-x-2">
                        <CheckCircle size={20} className="text-teal-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {recommendation.alternative && (
                  <div className="bg-gray-50 rounded-lg p-4 mt-4">
                    <p className="text-sm text-gray-600">
                      <strong>Alternative option:</strong> {recommendation.alternative}
                    </p>
                  </div>
                )}
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    onSelect(recommendation.serviceValue);
                    onClose();
                  }}
                  className="flex-1 bg-teal-600 text-white py-4 rounded-lg font-semibold hover:bg-teal-700 transition"
                >
                  Book {recommendation.service}
                </button>
                <button
                  onClick={onClose}
                  className="px-6 py-4 border-2 border-gray-300 rounded-lg font-semibold hover:border-gray-400 transition"
                >
                  See All Services
                </button>
              </div>

              <button
                onClick={() => {
                  setStep(1);
                  setAnswers({ concern: '', areas: [], pressure: '', experience: '' });
                  setRecommendation(null);
                }}
                className="w-full text-teal-600 hover:text-teal-700 font-medium"
              >
                ← Start Over
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
